import type { SiteConfig } from '../../src/site/types';
import { EDITOR as JP_EDITOR } from './data/editor';

/** japansnowwatch.com */
const config: SiteConfig = {
  id: 'jp',
  brand: {
    name: 'JapanSnowWatch',
    shortName: 'JapanSnow',
    logoText: { lead: 'JapanSnow', accent: 'Watch' },
    alternateNames: ['Japan Snow Watch', 'JapanSnow.cam'],
    description: {
      en: 'Live webcams, snow depth and weather forecasts for Japanese ski resorts.',
      ja: '日本のスキー場のライブカメラ、積雪、天気予報。',
      ko: '일본 스키장의 라이브 카메라, 적설, 날씨 예보.',
      zh: '日本滑雪场的实时摄像头、积雪深度和天气预报。',
    },
    sameAs: ['https://www.instagram.com/japansnowwatch/'],
    logoPath: '/favicon-japan.png',
    ogImagePath: '/og-image-japan.jpg',
    contactEmail: 'hello@japansnowwatch.com',
  },
  domain: 'japansnowwatch.com',
  origin: 'https://japansnowwatch.com',
  languages: ['en', 'ja', 'ko'],
  crawlerLinks: true,
  nav: [
    { href: '/blog/japan-snow-report', label: { en: 'Snow report', ja: '積雪レポート', ko: '적설 리포트', zh: '积雪报告' } },
    { href: '/private-luxury', label: { en: 'Private & luxury', ja: 'プライベート', ko: '프라이빗·럭셔리', zh: '私享奢华' } },
  ],
  ogLocales: { en: 'en_GB', ja: 'ja_JP', ko: 'ko_KR', zh: 'zh_CN' },
  currency: { code: 'JPY', symbol: '¥', decimals: 0 },
  timezone: 'Asia/Tokyo',
  timezoneLabel: 'JST',
  utcOffsetHours: 9,
  country: {
    code: 'JP',
    name: { en: 'Japan', ja: '日本', ko: '일본', zh: '日本' },
    adjective: { en: 'Japanese', ja: '日本の', ko: '일본', zh: '日本' },
  },
  // Prefectures. Order matters: normalizeRegion() returns the first match.
  regions: [
    { id: 'Hokkaido', group: 'Hokkaido', name: { en: 'Hokkaido', ja: '北海道', ko: '홋카이도', zh: '北海道' } },
    { id: 'Aomori', group: 'Tohoku', name: { en: 'Aomori', ja: '青森県', ko: '아오모리현', zh: '青森县' } },
    { id: 'Iwate', group: 'Tohoku', name: { en: 'Iwate', ja: '岩手県', ko: '이와테현', zh: '岩手县' } },
    { id: 'Akita', group: 'Tohoku', name: { en: 'Akita', ja: '秋田県', ko: '아키타현', zh: '秋田县' } },
    { id: 'Yamagata', group: 'Tohoku', name: { en: 'Yamagata', ja: '山形県', ko: '야마가타현', zh: '山形县' } },
    { id: 'Miyagi', group: 'Tohoku', name: { en: 'Miyagi', ja: '宮城県', ko: '미야기현', zh: '宫城县' } },
    { id: 'Fukushima', group: 'Tohoku', name: { en: 'Fukushima', ja: '福島県', ko: '후쿠시마현', zh: '福岛县' } },
    { id: 'Gunma', group: 'Kanto', name: { en: 'Gunma', ja: '群馬県', ko: '군마현', zh: '群马县' } },
    { id: 'Tochigi', group: 'Kanto', name: { en: 'Tochigi', ja: '栃木県', ko: '도치기현', zh: '栃木县' } },
    { id: 'Saitama', group: 'Kanto', name: { en: 'Saitama', ja: '埼玉県', ko: '사이타마현', zh: '埼玉县' } },
    { id: 'Tokyo', group: 'Kanto', name: { en: 'Tokyo', ja: '東京都', ko: '도쿄도', zh: '东京都' } },
    { id: 'Kanagawa', group: 'Kanto', name: { en: 'Kanagawa', ja: '神奈川県', ko: '가나가와현', zh: '神奈川县' } },
    { id: 'Chiba', group: 'Kanto', name: { en: 'Chiba', ja: '千葉県', ko: '지바현', zh: '千叶县' } },
    { id: 'Ibaraki', group: 'Kanto', name: { en: 'Ibaraki', ja: '茨城県', ko: '이바라키현', zh: '茨城县' } },
    { id: 'Niigata', group: 'Chubu', name: { en: 'Niigata', ja: '新潟県', ko: '니가타현', zh: '新潟县' } },
    { id: 'Nagano', group: 'Chubu', name: { en: 'Nagano', ja: '長野県', ko: '나가노현', zh: '长野县' } },
    { id: 'Gifu', group: 'Chubu', name: { en: 'Gifu', ja: '岐阜県', ko: '기후현', zh: '岐阜县' } },
    { id: 'Toyama', group: 'Chubu', name: { en: 'Toyama', ja: '富山県', ko: '도야마현', zh: '富山县' } },
    { id: 'Ishikawa', group: 'Chubu', name: { en: 'Ishikawa', ja: '石川県', ko: '이시카와현', zh: '石川县' } },
    { id: 'Fukui', group: 'Chubu', name: { en: 'Fukui', ja: '福井県', ko: '후쿠이현', zh: '福井县' } },
    { id: 'Yamanashi', group: 'Chubu', name: { en: 'Yamanashi', ja: '山梨県', ko: '야마나시현', zh: '山梨县' } },
    { id: 'Shizuoka', group: 'Chubu', name: { en: 'Shizuoka', ja: '静岡県', ko: '시즈오카현', zh: '静冈县' } },
    { id: 'Aichi', group: 'Chubu', name: { en: 'Aichi', ja: '愛知県', ko: '아이치현', zh: '爱知县' } },
    { id: 'Shiga', group: 'Kansai', name: { en: 'Shiga', ja: '滋賀県', ko: '시가현', zh: '滋贺县' } },
    { id: 'Kyoto', group: 'Kansai', name: { en: 'Kyoto', ja: '京都府', ko: '교토부', zh: '京都府' } },
    { id: 'Hyogo', group: 'Kansai', name: { en: 'Hyogo', ja: '兵庫県', ko: '효고현', zh: '兵库县' } },
    { id: 'Nara', group: 'Kansai', name: { en: 'Nara', ja: '奈良県', ko: '나라현', zh: '奈良县' } },
    { id: 'Wakayama', group: 'Kansai', name: { en: 'Wakayama', ja: '和歌山県', ko: '와카야마현', zh: '和歌山县' } },
    { id: 'Osaka', group: 'Kansai', name: { en: 'Osaka', ja: '大阪府', ko: '오사카부', zh: '大阪府' } },
    { id: 'Mie', group: 'Kansai', name: { en: 'Mie', ja: '三重県', ko: '미에현', zh: '三重县' } },
    { id: 'Tottori', group: 'Chugoku', name: { en: 'Tottori', ja: '鳥取県', ko: '돗토리현', zh: '鸟取县' } },
    { id: 'Shimane', group: 'Chugoku', name: { en: 'Shimane', ja: '島根県', ko: '시마네현', zh: '岛根县' } },
    { id: 'Okayama', group: 'Chugoku', name: { en: 'Okayama', ja: '岡山県', ko: '오카야마현', zh: '冈山县' } },
    { id: 'Hiroshima', group: 'Chugoku', name: { en: 'Hiroshima', ja: '広島県', ko: '히로시마현', zh: '广岛县' } },
    { id: 'Yamaguchi', group: 'Chugoku', name: { en: 'Yamaguchi', ja: '山口県', ko: '야마구치현', zh: '山口县' } },
    { id: 'Tokushima', group: 'Shikoku', name: { en: 'Tokushima', ja: '徳島県', ko: '도쿠시마현', zh: '德岛县' } },
    { id: 'Ehime', group: 'Shikoku', name: { en: 'Ehime', ja: '愛媛県', ko: '에히메현', zh: '爱媛县' } },
    { id: 'Kagawa', group: 'Shikoku', name: { en: 'Kagawa', ja: '香川県', ko: '가가와현', zh: '香川县' } },
    { id: 'Kochi', group: 'Shikoku', name: { en: 'Kochi', ja: '高知県', ko: '고치현', zh: '高知县' } },
    { id: 'Fukuoka', group: 'Kyushu', name: { en: 'Fukuoka', ja: '福岡県', ko: '후쿠오카현', zh: '福冈县' } },
    { id: 'Saga', group: 'Kyushu', name: { en: 'Saga', ja: '佐賀県', ko: '사가현', zh: '佐贺县' } },
    { id: 'Nagasaki', group: 'Kyushu', name: { en: 'Nagasaki', ja: '長崎県', ko: '나가사키현', zh: '长崎县' } },
    { id: 'Oita', group: 'Kyushu', name: { en: 'Oita', ja: '大分県', ko: '오이타현', zh: '大分县' } },
    { id: 'Kumamoto', group: 'Kyushu', name: { en: 'Kumamoto', ja: '熊本県', ko: '구마모토현', zh: '熊本县' } },
    { id: 'Miyazaki', group: 'Kyushu', name: { en: 'Miyazaki', ja: '宮崎県', ko: '미야자키현', zh: '宫崎县' } },
    { id: 'Kagoshima', group: 'Kyushu', name: { en: 'Kagoshima', ja: '鹿児島県', ko: '가고시마현', zh: '鹿儿岛县' } },
  ],
  regionLabels: {
    Hokkaido: { ja: '北海道', en: 'Hokkaido', ko: '홋카이도' },
    Nagano: { ja: '長野県', en: 'Nagano', ko: '나가노현' },
    Niigata: { ja: '新潟県', en: 'Niigata', ko: '니가타현' },
    Tohoku: { ja: '東北', en: 'Tohoku', ko: '토호쿠' },
  },
  regionAliases: {
    'yuzawa & minamiuonuma': 'Niigata',
    'shiga kogen': 'Nagano',
    hakuba: 'Nagano',
  },
  map: { center: [38.5, 139.0], zoom: 7, bounds: [[24.0, 122.9], [45.6, 146.0]] },
  analytics: {
    ga4Id: 'G-VKLMLGZJHY',
    adsenseClient: 'ca-pub-3385716674270944',
    searchConsoleVerification: ['hhM770yOXZ6Kh55Ya3MDtMgaKB0FiyxmQV6n3xvCthw', 'jIsaWlScXmDkg6hFBVPCNs8TnvNeh-FGLrYYtrjrnkM'],
    // Naver Search Advisor ownership check (owner added the site on 2026-10-25).
    extraMeta: [{ name: 'naver-site-verification', content: '5777689d685309fd0d4df3f692dd19ec9b1726a5' }],
  },
  supabase: {
    url: 'https://koqicactsetakozoahvh.supabase.co',
    publishableKey: 'sb_publishable_4vvTdgjilt8UUK7xYw6-WQ_FCkNtpho',
    projectId: 'koqicactsetakozoahvh',
  },
  originCity: { ja: '東京', en: 'Tokyo', ko: '도쿄', zh: '东京' },
  railBrand: { ja: '新幹線', en: 'Shinkansen', ko: '신칸센', zh: '新干线' },
  transportOrigins: [
    { id: 'tokyo', name: { ja: '東京', en: 'Tokyo', ko: '도쿄', zh: '东京' }, coords: [35.6762, 139.6503], airports: ['HND', 'NRT'] },
    { id: 'osaka', name: { ja: '大阪', en: 'Osaka', ko: '오사카', zh: '大阪' }, coords: [34.6937, 135.5023], airports: ['KIX', 'ITM'] },
    { id: 'nagoya', name: { ja: '名古屋', en: 'Nagoya', ko: '나고야', zh: '名古屋' }, coords: [35.1815, 136.9066], airports: ['NGO'] },
    { id: 'sapporo', name: { ja: '札幌', en: 'Sapporo', ko: '삿포로', zh: '札幌' }, coords: [43.0618, 141.3545], airports: ['CTS'] },
    { id: 'fukuoka', name: { ja: '福岡', en: 'Fukuoka', ko: '후쿠오카', zh: '福冈' }, coords: [33.5904, 130.4017], airports: ['FUK'] },
  ],
  features: {
    racetracks: false,
    x5Pass: false,
    muslimFriendly: false,
    skicast: true,
    currencyConverter: true,
    snowForecast: true,
    monthlySnowForecast: true,
    winterTraffic: true,
    seasonCalendar: true,
    bestResorts: true,
    snowReportSatelliteMap: true,
    snowReportTables: true,
  },
  routes: {
    bestResorts: 'best-ski-resorts-japan',
    flagship: { slug: 'furano', resortSlug: 'furano' },
  },
  extraRoutes: [
    { path: 'ski-seo-guide', page: 'JaSkiSeoGuidePage', languages: ['ja'] },
    { path: 'ski-faq', page: 'JaSkiFaqHubPage', languages: ['ja'] },
    // 2026-27 opening dates on top of the shared season calendar.
    { path: 'season-calendar', page: 'JpSeasonCalendarPage' },
    // Where the data comes from and how it is checked (data/methodology.ts).
    { path: 'methodology', page: 'JpMethodologyPage' },
    // Hub for the luxury and private-experience guides (data/luxuryHub.ts).
    { path: 'private-luxury', page: 'JpLuxuryHubPage' },
    // The editor's page, only while the flag in data/editor.ts is on.
    ...(JP_EDITOR.enabled ? [{ path: 'about/editor', page: 'JpEditorPage' }] : []),
  ],
  gettingThere: { href: '/blog/day-trips-from-tokyo', label: { en: 'Getting there', ja: '行き方', ko: '가는 법', zh: '如何前往' } },
  crossSitePromos: [
    {
      site: 'kr',
      url: 'https://koreasnowwatch.com/',
      name: { en: 'KoreaSnowWatch', ja: 'KoreaSnowWatch', ko: 'KoreaSnowWatch', zh: 'KoreaSnowWatch' },
      blurb: { en: 'Korean ski resorts', ja: '韓国のスキー場', ko: '한국 스키장', zh: '韩国滑雪场' },
      cta: { en: 'Ski Korea', ja: '韓国でスキー', ko: '한국에서 스키', zh: '去韩国滑雪' },
      languages: ['ko', 'en', 'ja', 'zh'],
    },
    {
      site: 'cn',
      url: 'https://chinasnowwatch.com/',
      name: { en: 'ChinaSnowWatch', ja: 'ChinaSnowWatch', ko: 'ChinaSnowWatch', zh: 'ChinaSnowWatch' },
      blurb: { en: 'Chinese ski resorts', ja: '中国のスキー場', ko: '중국 스키장', zh: '中国滑雪场' },
      cta: { en: 'Ski China', ja: '中国でスキー', ko: '중국에서 스키', zh: '去中国滑雪' },
      languages: ['en', 'zh', 'ko', 'ja'],
    },
  ],
  storageKeys: {
    cookieConsent: 'jsc-cookie-consent',
    language: 'japansnow-language',
    currency: 'japansnow-currency',
    rates: 'japansnow-rates',
  },
  seo: {
    title: 'Japan Ski Resort Webcams & Snow Forecast | JapanSnowWatch',
    description:
      'Live webcams, snow depth and weather for Japanese ski resorts: Niseko, Hakuba, Nozawa Onsen, GALA Yuzawa and hundreds more.',
    keywords:
      'japan ski resort webcam, ski japan webcam, snow forecast japan, japanese ski resorts, japan snow conditions, niseko webcam, hakuba webcam, nozawa onsen ski, japan powder snow, hokkaido ski, nagano ski resort, gala yuzawa, myoko kogen, shiga kogen, rusutsu, furano, appi kogen, zao onsen, japan ski pass, japow, best powder japan, 日本スキー場, スキー場ウェブカメラ, スキー場ライブカメラ, 雪予報, 積雪情報, ニセコ, 白馬, 野沢温泉, ガーラ湯沢, 妙高高原, 志賀高原, ルスツ, 富良野, 蔵王温泉, パウダースノー, 日本スキー旅行, ゲレンデ状況',
    ogTitle: 'Japan Ski Resort Webcams & Snow Forecast | JapanSnowWatch',
    ogDescription: 'Live webcams, snow depth and weather forecasts for Japanese ski resorts.',
    twitterTitle: 'Japan Ski Resort Webcams & Snow Forecast',
    twitterDescription: 'Live webcams, snow depth and weather forecasts for Japanese ski resorts: Niseko, Hakuba, GALA Yuzawa and more.',
    htmlLang: 'ja',
    ogLocale: 'ja_JP',
    ogLocaleAlternates: ['en_GB', 'ko_KR'],
    shellHeading: 'Japan Ski Resort Webcams & Snow Forecast',
    shellParagraphs: [
      'Live webcams, snow depth and weather forecasts for ski resorts across Japan, from Hokkaido and Tohoku to Nagano, Niigata and further south.',
      'Webcam stills sit next to snow depth, temperature, wind speed and a seven-day snowfall forecast, so you can see conditions before committing to a drive.',
    ],
    baseKeywords: 'japan ski resort, ski japan, japan snow, niseko, hakuba, powder snow, japow, 日本スキー場, スキー場ライブカメラ, 일본 스키장',
    rootTitle: 'Japan Ski Resort Webcams & Snow Forecast',
    rootDescription:
      'Live webcams, snow depth and weather forecasts for ski resorts across Japan, from Hokkaido and Tohoku to Nagano, Niigata and further south.',
    notFoundDescription:
      "The page you're looking for doesn't exist or has moved. Browse Japanese ski resorts, live webcams and snow reports below.",
  },
  popularResorts: ['niseko-hirafu', 'happo-one', 'rusutsu', 'nozawa-onsen', 'shiga-kogen', 'gala-yuzawa', 'myoko', 'zao', 'furano', 'hakuba-cortina'],
  weeklyReportSlugs: ['japan-snow-report'],
};

export default config;
